import { useState } from "react";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { timeBoards, type BoardItem } from "@/mocks/home";

type BoardKey = "day" | "week" | "month";

const tabs: { key: BoardKey; label: string; note: string }[] = [
  { key: "day", label: "日榜", note: "过去 24 小时" },
  { key: "week", label: "周榜", note: "近 7 天累计" },
  { key: "month", label: "月榜", note: "本月累计" },
];

function Trend({ trend }: { trend: BoardItem["trend"] }) {
  if (trend === "up") {
    return <i className="ri-arrow-up-s-fill text-base text-primary-500" />;
  }
  if (trend === "down") {
    return <i className="ri-arrow-down-s-fill text-base text-foreground-400" />;
  }
  return <i className="ri-subtract-line text-base text-secondary-500" />;
}

export default function TimeBoardTabs() {
  const [active, setActive] = useState<BoardKey>("day");
  const currentTab = tabs.find((t) => t.key === active);
  const list = timeBoards[active];

  return (
    <section id="boards" className="bg-background-50">
      <div className="mx-auto max-w-[1200px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
          <div>
            <SectionHeading
              index="03"
              eyebrow="Time Boards"
              title="31黑料网黑料榜单 · 分时段榜"
              desc="同一件事在不同时间段的热度表现并不一样。切换时段，可以看到条目是从哪个节点开始升温、又在什么时候回落。"
            />
            <div className="flex flex-col gap-3">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActive(tab.key)}
                  className={`flex cursor-pointer items-center justify-between rounded-lg border px-4 py-3.5 text-left transition-colors ${
                    active === tab.key
                      ? "border-primary-300 bg-primary-50"
                      : "border-background-200 bg-background-50 hover:border-background-300"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-md ${
                        active === tab.key
                          ? "bg-primary-500 text-background-50"
                          : "bg-secondary-100 text-secondary-700"
                      }`}
                    >
                      <i
                        className={
                          tab.key === "day"
                            ? "ri-sun-line text-base"
                            : tab.key === "week"
                              ? "ri-calendar-line text-base"
                              : "ri-calendar-2-line text-base"
                        }
                      />
                    </span>
                    <span>
                      <span className="block font-heading text-[15px] font-bold text-foreground-950">
                        {tab.label}
                      </span>
                      <span className="block text-xs text-foreground-500">{tab.note}</span>
                    </span>
                  </span>
                  <i
                    className={`text-lg ${
                      active === tab.key ? "ri-checkbox-circle-fill text-primary-500" : "ri-circle-line text-foreground-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-foreground-950">
                {currentTab?.label} · 热度TOP
              </h3>
              <span className="rounded-full bg-accent-100 px-3 py-1 text-[11px] font-medium text-accent-900">
                {currentTab?.note}
              </span>
            </div>
            <div className="flex flex-col">
              {list.map((item, idx) => (
                <div
                  key={`${active}-${item.rank}`}
                  className={`flex items-center gap-4 py-3.5 ${
                    idx !== list.length - 1 ? "border-b border-background-300/70" : ""
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                      item.rank <= 3
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {item.rank}
                  </span>
                  <span className="flex-1 truncate text-[14px] font-medium text-foreground-900">
                    {item.title}
                  </span>
                  <span className="flex w-6 items-center justify-center">
                    <Trend trend={item.trend} />
                  </span>
                  <span className="w-20 text-right font-heading text-[15px] font-bold text-accent-600">
                    {item.heat}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 border-t border-background-300/70 pt-4 text-xs leading-relaxed text-foreground-500">
              榜单每小时重算一次，历史数据保留 90 天，便于对比同一事件在不同时段的热度曲线。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}